'use client';

import { useEffect, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { onSlotRenderEnded } from '@/src/lib/ads/gpt';

// GPT's 805px creative container would otherwise widen the cell's 1fr column.
const adSlotItem = css({
  display: 'flex',
  minWidth: 0,
});

// An unfilled slot must keep its container in the DOM: the slot stays defined
// and `pubads().refresh` keeps re-requesting it, and GPT can only render into a
// div that still exists. Unmounting it — what this did before — turned every
// later request on that slot into one GAM could never fill. Height, not
// `display`, so the container keeps its width and GPT still measures a `fluid`
// creative correctly on the refresh that fills it.
const adSlotItemUnfilled = css({
  height: 0,
  overflow: 'hidden',
});

const adSlotSurface = css({
  minWidth: 0,
  width: '100%',
  // eslint-disable-next-line hotpads/no-magic-numbers -- legacy's SRP ad card height (AdvertisementWrapper/styles.tsx)
  minHeight: '360px',
  // The slot requests a `fluid` size, which GPT measures from the creative's own
  // content. Stretching the iframe into a fixed box instead — what this did
  // before — crops taller creatives and squashes the 805x115 banner.
  '& iframe': {
    // The creative owns its bottom border, so only clip the host-owned top corners.
    clipPath: 'inset(0 round token(radii.md) token(radii.md) 0 0)',
    maxWidth: '100%',
  },
});

const adSlotSurfaceUnfilled = css({
  minHeight: 0,
});

type Props = {
  id: string;
  /** Style hook for the feed's snap slot; the grid uses the default cell. */
  className?: string;
};

export function SrpAdSlot({ id, className }: Props) {
  const [isUnfilled, setIsUnfilled] = useState(false);

  // GPT's `collapseEmptyDivs` only hides its own div, which would leave this
  // grid cell as a hole between listing cards. Every render end reports in, so a
  // later refresh that fills the slot expands it again.
  useEffect(() => onSlotRenderEnded(id, setIsUnfilled), [id]);

  return (
    <li
      data-srp-ad-unfilled={isUnfilled ? 'true' : 'false'}
      className={cx(className ?? adSlotItem, isUnfilled ? adSlotItemUnfilled : undefined)}
    >
      <div id={id} className={cx(adSlotSurface, isUnfilled ? adSlotSurfaceUnfilled : undefined)} />
    </li>
  );
}
